/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

type HookProps = {
  api: vp.Api;
};

export type AccountStatementRequest = {
  invoiceId: string;
};

export type AccountStatementPDFResponse = {
  success: boolean;
  errorMessage: string;
  statementPDF: string;
};

type ReturnTuple = [
  AccountStatementPDFResponse | undefined,
  (requestParams: AccountStatementRequest) => void,
  () => void,
];

function useFetchAccountStatementPDF({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<AccountStatementRequest>();
  const [response, setResponse] = useState<AccountStatementPDFResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: AccountStatementRequest) => {
      setDataFetchingInfo(true, 'creating pdf', 'fullScreen');
      const { Success, ErrorMessage, InvoicePDF } = await api.fetchAccountStatementPDF(
        buildParams(requestParams),
        source,
      );

      const res = {
        success: Success,
        errorMessage: ErrorMessage,
        statementPDF: InvoicePDF,
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: AccountStatementRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: AccountStatementRequest): vp.StatementCsvRequest {
    return {
      InvoiceID: requestParams.invoiceId,
    };
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchAccountStatementPDF);
